import { useState } from "react";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Clock, CheckCircle2, Loader2, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { COMPANY, CALLBACK_TIMES } from "@/data/content";
import { submitLead } from "@/lib/api";

const EMPTY = { name: "", phone: "", email: "", street: "", city: "", callback_time: "", message: "", company: "" };

export default function ContactSection() {
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim() || form.name.trim().length < 2) e.name = "Bitte geben Sie Ihren Namen an.";
    if (!form.phone.trim() || form.phone.trim().length < 4) e.phone = "Bitte geben Sie eine Telefonnummer an.";
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Bitte eine gültige E-Mail angeben.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) {
      toast.error("Bitte prüfen Sie Ihre Angaben.");
      return;
    }
    setLoading(true);
    try {
      const res = await submitLead(form);
      setDone(true);
      setForm(EMPTY);
      toast.success(res.message || "Vielen Dank! Wir melden uns bei Ihnen.");
    } catch (err) {
      toast.error("Es ist ein Fehler aufgetreten. Bitte rufen Sie uns direkt an.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="kontakt" className="section-pad bg-white" data-testid="section-kontakt">
      <div className="container-x">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-start">
          {/* Left: reassurance + hotline */}
          <div className="order-2 lg:order-1">
            <span className="text-sm font-bold tracking-wide text-[color:var(--brand-green-alt)] uppercase">Kontakt</span>
            <h2 className="mt-2 font-display text-4xl sm:text-5xl text-[color:var(--brand-navy)]">Für mehr Sicherheit – wir sind für Sie da!</h2>
            <p className="mt-3 text-base sm:text-lg text-muted-foreground">Fordern Sie einen kostenlosen Rückruf an oder rufen Sie uns direkt an. Wir beraten Sie unverbindlich und persönlich.</p>

            <a
              href={`tel:${COMPANY.phoneHref}`}
              data-testid="contact-hotline-link"
              className="mt-6 flex items-center gap-4 rounded-2xl bg-[color:var(--brand-navy)] p-5 text-white card-shadow hover:bg-[color:var(--brand-navy-alt)] transition-colors"
            >
              <span className="inline-flex h-14 w-14 items-center justify-center rounded-xl bg-[color:var(--brand-green)]">
                <Phone className="h-7 w-7 text-white" />
              </span>
              <span>
                <span className="block text-sm text-white/75">Direkt anrufen – 24h Beratung</span>
                <span className="block text-2xl sm:text-3xl font-display">{COMPANY.phoneDisplay}</span>
              </span>
            </a>

            <a
              href={`https://wa.me/${COMPANY.whatsappHref}?text=${encodeURIComponent(COMPANY.whatsappMsg)}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-whatsapp-link"
              className="mt-3 flex items-center gap-4 rounded-2xl bg-white border-2 border-[#25D366] p-4 hover:bg-[#25D366]/5 transition-colors"
            >
              <span className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-[#25D366] text-white">
                <svg viewBox="0 0 32 32" className="h-7 w-7" fill="currentColor" aria-hidden="true">
                  <path d="M16.004 3.2c-7.06 0-12.8 5.74-12.8 12.8 0 2.26.6 4.46 1.74 6.4L3.2 28.8l6.56-1.72a12.74 12.74 0 0 0 6.24 1.62h.01c7.06 0 12.8-5.74 12.8-12.8s-5.75-12.7-12.81-12.7Zm0 23.06h-.01a10.6 10.6 0 0 1-5.4-1.48l-.39-.23-3.89 1.02 1.04-3.79-.25-.39a10.56 10.56 0 0 1-1.62-5.62c0-5.86 4.77-10.63 10.64-10.63 2.84 0 5.51 1.11 7.52 3.12a10.56 10.56 0 0 1 3.11 7.52c0 5.87-4.77 10.63-10.62 10.63Zm5.83-7.96c-.32-.16-1.89-.93-2.18-1.04-.29-.11-.5-.16-.71.16-.21.32-.82 1.04-1.01 1.25-.19.21-.37.24-.69.08-.32-.16-1.35-.5-2.57-1.59-.95-.85-1.59-1.89-1.78-2.21-.19-.32-.02-.49.14-.65.14-.14.32-.37.48-.56.16-.19.21-.32.32-.53.11-.21.05-.4-.03-.56-.08-.16-.71-1.72-.98-2.35-.26-.62-.52-.54-.71-.55l-.61-.01c-.21 0-.56.08-.85.4-.29.32-1.12 1.09-1.12 2.66 0 1.57 1.14 3.08 1.3 3.29.16.21 2.24 3.42 5.43 4.8.76.33 1.35.52 1.81.67.76.24 1.45.21 2 .13.61-.09 1.89-.77 2.16-1.52.27-.75.27-1.39.19-1.52-.08-.13-.29-.21-.61-.37Z"/>
                </svg>
              </span>
              <span>
                <span className="block text-sm text-muted-foreground">Schnell & unkompliziert</span>
                <span className="block text-lg font-bold text-[color:var(--brand-navy)]">Per WhatsApp schreiben</span>
              </span>
            </a>

            <ul className="mt-6 space-y-3 text-[15px] text-[color:var(--brand-navy)]">
              <li className="flex items-center gap-3"><MapPin className="h-5 w-5 text-[color:var(--brand-green-alt)]" /> {COMPANY.street}, {COMPANY.zip} {COMPANY.city}</li>
              <li className="flex items-center gap-3"><Mail className="h-5 w-5 text-[color:var(--brand-green-alt)]" /> <a className="hover:underline" href={`mailto:${COMPANY.email}`}>{COMPANY.email}</a></li>
              <li className="flex items-center gap-3"><Clock className="h-5 w-5 text-[color:var(--brand-green-alt)]" /> Notrufzentrale rund um die Uhr erreichbar</li>
            </ul>

            <div className="mt-6 flex items-center gap-2 text-sm text-muted-foreground">
              <ShieldCheck className="h-5 w-5 text-[color:var(--brand-green-alt)]" /> Ihre Daten werden vertraulich behandelt.
            </div>
          </div>

          {/* Right: form */}
          <div className="order-1 lg:order-2">
            <div className="rounded-3xl border border-border bg-white p-5 sm:p-8 card-shadow">
              {done ? (
                <div className="text-center py-10" data-testid="contact-success">
                  <span className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-[color:var(--brand-green)]/15">
                    <CheckCircle2 className="h-9 w-9 text-[color:var(--brand-green-alt)]" />
                  </span>
                  <h3 className="mt-4 text-2xl font-bold text-[color:var(--brand-navy)]">Vielen Dank!</h3>
                  <p className="mt-2 text-muted-foreground">Ihre Anfrage ist bei uns eingegangen. Wir melden uns schnellstmöglich bei Ihnen zurück.</p>
                  <Button onClick={() => setDone(false)} variant="outline" className="mt-6 rounded-xl">Weitere Anfrage senden</Button>
                  <p className="mt-4 text-sm">Oder direkt anrufen: <a href={`tel:${COMPANY.phoneHref}`} className="font-bold text-[color:var(--brand-navy)]">{COMPANY.phoneDisplay}</a></p>
                </div>
              ) : (
                <form onSubmit={onSubmit} noValidate data-testid="contact-form">
                  <h3 className="text-xl font-bold text-[color:var(--brand-navy)]">Rückruf anfordern</h3>
                  <p className="text-sm text-muted-foreground mt-1">Wir rufen Sie zum gewünschten Zeitpunkt zurück.</p>

                  {/* honeypot */}
                  <input type="text" name="company" value={form.company} onChange={(e) => set("company", e.target.value)} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />

                  <div className="mt-5 grid grid-cols-1 gap-4">
                    <div>
                      <Label htmlFor="name" className="font-semibold text-[color:var(--brand-navy)]">Name *</Label>
                      <Input id="name" data-testid="contact-form-name-input" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Vor- und Nachname" className="mt-1.5 h-12" aria-invalid={!!errors.name} />
                      {errors.name && <p className="mt-1 text-sm text-destructive" role="alert">{errors.name}</p>}
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="phone" className="font-semibold text-[color:var(--brand-navy)]">Telefon *</Label>
                        <Input id="phone" data-testid="contact-form-phone-input" value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="z. B. 0170 1234567" className="mt-1.5 h-12" inputMode="tel" aria-invalid={!!errors.phone} />
                        {errors.phone && <p className="mt-1 text-sm text-destructive" role="alert">{errors.phone}</p>}
                      </div>
                      <div>
                        <Label htmlFor="email" className="font-semibold text-[color:var(--brand-navy)]">E-Mail</Label>
                        <Input id="email" data-testid="contact-form-email-input" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="name@beispiel.de" className="mt-1.5 h-12" inputMode="email" aria-invalid={!!errors.email} />
                        {errors.email && <p className="mt-1 text-sm text-destructive" role="alert">{errors.email}</p>}
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="street" className="font-semibold text-[color:var(--brand-navy)]">Straße und Hausnummer</Label>
                      <Input id="street" data-testid="contact-form-street-input" value={form.street} onChange={(e) => set("street", e.target.value)} placeholder="Musterstraße 12" className="mt-1.5 h-12" />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="city" className="font-semibold text-[color:var(--brand-navy)]">PLZ / Ort</Label>
                        <Input id="city" data-testid="contact-form-city-input" value={form.city} onChange={(e) => set("city", e.target.value)} placeholder="44135 Dortmund" className="mt-1.5 h-12" />
                      </div>
                      <div>
                        <Label className="font-semibold text-[color:var(--brand-navy)]">Gewünschte Rückrufzeit</Label>
                        <Select value={form.callback_time} onValueChange={(v) => set("callback_time", v)}>
                          <SelectTrigger data-testid="contact-form-callback-select" className="mt-1.5 h-12">
                            <SelectValue placeholder="Bitte wählen" />
                          </SelectTrigger>
                          <SelectContent>
                            {CALLBACK_TIMES.map((t) => (
                              <SelectItem key={t} value={t}>{t}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="message" className="font-semibold text-[color:var(--brand-navy)]">Nachricht</Label>
                      <Textarea id="message" data-testid="contact-form-message-input" value={form.message} onChange={(e) => set("message", e.target.value)} placeholder="Ihre Nachricht oder Frage (optional)" className="mt-1.5 min-h-[110px]" />
                    </div>

                    <Button type="submit" disabled={loading} data-testid="contact-form-submit-button" className="h-14 rounded-xl bg-[color:var(--brand-green)] hover:bg-[color:var(--brand-green-alt)] text-white text-base font-bold">
                      {loading ? <><Loader2 className="h-5 w-5 mr-2 animate-spin" /> Wird gesendet…</> : "Rückruf anfordern"}
                    </Button>
                    <p className="text-xs text-muted-foreground text-center">Mit dem Absenden stimmen Sie unserer <a href="/datenschutz" className="underline">Datenschutzerklärung</a> zu. * Pflichtfelder.</p>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
